import { faUser } from '@fortawesome/pro-solid-svg-icons';

import { IconBadge, IconBadgeUse, Text, useRebrand } from '@growtherapy/sprout-ui';

import { AppointmentTime, type AppointmentTimeProps } from '../components/AppointmentTime';
import { Toast } from '../toasts/Toast';
import { ToastButton } from '../toasts/ToastButton';
import { ToastRenderFnProps } from '../toasts/types';
import { VisitorPresence } from '../types';
import { noop } from '../utils';
import { JoiningClientLoader } from './admission/JoiningClientLoader';
import { type AdmittedClientStatusResult } from './admission/useAdmittedClientStatus';

export type ClientQueueToastProps = {
  admittedClientStatus?: AdmittedClientStatusResult;
  appointment?: AppointmentTimeProps['appointment'];
  buttonIsLoading?: boolean;
  currentSessionPatientShortId: VisitorPresence['patientShortId'] | null;
  isAppointmentLoading?: AppointmentTimeProps['isLoading'];
  isIncompatible?: boolean;
  isZoom?: boolean;
  onAdmit: () => Promise<void>;
  onOpenQueue: () => void;
  /**
   * unlike onAdmit, this callback is required to avoid a funky edge-case when a
   * client with a different shortId joins the waiting room while the provider
   * is in a session with another client. if this becomes too cumbersome,
   * consider using onOpenQueue when the client is in a session with another
   * client
   */
  onShowStartNewSessionConfirmation: () => void;
  participantName: string;
  patientShortId: VisitorPresence['patientShortId'];
  visitorUuid?: string;
} & ToastRenderFnProps;

export function ClientQueueToast({
  admittedClientStatus,
  appointment,
  isAppointmentLoading,
  buttonIsLoading,
  currentSessionPatientShortId,
  isIncompatible,
  isZoom,
  onAdmit,
  onOpenQueue = noop,
  onShowStartNewSessionConfirmation,
  participantName,
  patientShortId,
  visitorUuid,
  contentProps,
  descriptionProps,
  titleProps,
  ...toastAriaProps
}: ClientQueueToastProps) {
  const isRebrand = useRebrand();
  const verbVariant = visitorUuid ? 'has' : 'have';
  const isRequestForNewSession =
    isIncompatible || (!!currentSessionPatientShortId && currentSessionPatientShortId !== patientShortId);

  const onButtonClick = async () => {
    // if a new session has been requested (e.g., patientShortId is not the
    // same as the current session patientShortId), show a confirmation
    if (isRequestForNewSession) {
      onShowStartNewSessionConfirmation();
      return;
    }

    if (visitorUuid) {
      await onAdmit();
      return;
    }

    onOpenQueue();
  };

  return (
    <Toast className='sm:w-[30rem]' data-testid='client-queue-toast' closeButtonSize='sm' {...toastAriaProps}>
      <button className='flex items-center flex-1 gap-3' onClick={onOpenQueue} aria-label='Open client queue'>
        <IconBadge
          aria-hidden
          icon={faUser}
          use={IconBadgeUse.Lilac}
          size='lg'
          className='arc:bg-surface-default'
          iconClassName='arc:text-text-default'
        />
        <div className='flex flex-col' {...contentProps}>
          <Text
            as='span'
            variant='sm'
            className='text-left fs-exclude'
            data-dd-privacy='mask'
            data-dd-action-name='Click on queue toast title'
            {...titleProps}
          >
            {participantName} {verbVariant} joined your waiting room
          </Text>
          <div className='flex flex-col' {...descriptionProps}>
            {isRequestForNewSession && (
              <AppointmentTime
                appointment={appointment}
                data-dd-action-name='appointment time'
                isLoading={isAppointmentLoading}
              />
            )}
          </div>
        </div>
      </button>
      {buttonIsLoading && admittedClientStatus ? (
        <JoiningClientLoader admittedClientStatus={admittedClientStatus} visitorName={participantName} />
      ) : (
        <ToastButton loading={buttonIsLoading} onClick={onButtonClick} use={isRebrand ? 'secondary' : 'primary'}>
          {visitorUuid ? (isZoom ? 'Invite in' : 'Admit') : 'View'}
        </ToastButton>
      )}
    </Toast>
  );
}
